
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SimulationConfig } from './types';
import { GeneralDisplaySection } from './GeneralDisplaySection';
import { ModeSelectorSection } from './ModeSelectorSection';
import { EscapeSettingsSection } from './EscapeSettingsSection';
import { PhysicsTab } from './PhysicsTab';
import { VisualsTab } from './VisualsTab';
import { EscapeAdvancedSection } from './EscapeAdvancedSection';
import { DrawerAdvancedSection } from './DrawerAdvancedSection';
import { SpawnerSettingsSection } from './SpawnerSettingsSection';
import { SphereDisplaySettings } from './SphereDisplaySettings';
import { TrailSettingsSection } from './TrailSettingsSection';
import { AudioStudioTab } from './AudioStudioTab';
import { StorageManager } from './StorageManager';
import { CameraSettingsSection } from './CameraSettingsSection';
import { ImpactFXSection } from './ImpactFXSection';
import { RhythmicModeSection } from './RhythmicModeSection';
import { ShapeSettingsSection } from './ShapeSettingsSection';
import { LayerSortingSection } from './LayerSortingSection';
import { HyperSpeedSettingsSection } from './HyperSpeedSettingsSection';
import { SpaceSpiralSettingsSection } from './SpaceSpiralSettingsSection';
import { BackgroundSettingsSection } from './BackgroundSettingsSection';
import { ParallaxSettingsSection } from './ParallaxSettingsSection';
import { OptimizationSettingsSection } from './OptimizationSettingsSection';
import { BallPlacementSection } from './BallPlacementSection';
import { RhythmicMidiFilterSection } from './RhythmicMidiFilterSection';
import { HUDSettingsSection } from './HUDSettingsSection';
import { RhythmicEscapeSettingsSection } from './RhythmicEscapeSettingsSection';
import { TitleFooterSettingsSection } from './TitleFooterSettingsSection';
import { SettingsSearchContext } from './SettingsContext';
import { CollapsibleSection } from './CollapsibleSection';

interface SettingsPanelProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  showUI: boolean;
  isPlaying: boolean;
  setIsPlaying: (p: boolean) => void;
  onReset: () => void;
  onSave: () => void;
  saveStatus: string;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAnalyzing: boolean;
  analysisProgress?: number;
  onBallSoundUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingBallSound: boolean;
  cloudSaves: any[];
  setCloudSaves: React.Dispatch<React.SetStateAction<any[]>>;
}

type SimSubTab = 'GAMEPLAY' | 'PHYSICS' | 'LOOKS' | 'VIEW' | 'OPTIMIZATION';

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  config, setConfig, showUI, isPlaying, setIsPlaying, onReset, onSave, saveStatus, onFileUpload, isAnalyzing, analysisProgress = 0, onBallSoundUpload, isUploadingBallSound,
  cloudSaves, setCloudSaves
}) => {
  const [activeTab, setActiveTab] = useState<'SIM' | 'AUDIO' | 'DATA'>('SIM');
  const [activeSimSubTab, setActiveSimSubTab] = useState<SimSubTab>('GAMEPLAY');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    (window as any).setConfig = setConfig;
  }, [setConfig]);

  const SimSubTabButton = ({ id, label, icon }: { id: SimSubTab, label: string, icon: string }) => (
      <button 
        onClick={() => setActiveSimSubTab(id)} 
        className={`flex-1 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all duration-200 border ${
            activeSimSubTab === id 
            ? (config.darkMode ? 'bg-white/10 border-white/20 text-white shadow-[0_0_15px_rgba(255,255,255,0.05)]' : 'bg-white border-neutral-300 text-neutral-900 shadow-sm') 
            : 'border-transparent opacity-40 hover:opacity-100 hover:bg-black/5'
        }`}
      >
          <i className={`fa-solid ${icon} text-[10px] mb-1.5`}></i>
          <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
      </button>
  );

  return (
    <SettingsSearchContext.Provider value={searchQuery}>
    <div className="absolute top-0 right-0 h-full flex items-center p-6 z-20 pointer-events-none">
      <motion.div 
        initial={false}
        animate={{ 
          x: showUI ? 0 : 500,
          opacity: showUI ? 1 : 0,
          scale: showUI ? 1 : 0.95
        }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className={`flex flex-col gap-2 shadow-2xl border pointer-events-auto max-h-[92vh] overflow-y-auto overflow-x-hidden custom-scrollbar w-[460px] hardware-surface p-7 scroll-smooth`}
      >
        
        {/* Search Bar */}
        <div className="w-full mb-3">
            <div className={`flex items-center px-4 py-3.5 rounded-2xl border transition-all ${config.darkMode ? 'bg-black/40 border-white/10 focus-within:border-white/30 focus-within:bg-black/60 shadow-inner' : 'bg-neutral-100 border-neutral-200 focus-within:bg-white focus-within:border-neutral-300'}`}>
                <i className="fa-solid fa-search opacity-40 text-xs mr-3"></i>
                <input 
                    type="text" 
                    placeholder="Search parameters..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-transparent outline-none w-full text-xs font-bold tracking-tight placeholder:opacity-30"
                />
                <AnimatePresence>
                  {searchQuery && (
                      <motion.button 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={() => setSearchQuery('')} 
                        className="opacity-40 hover:opacity-100 p-1"
                      >
                          <i className="fa-solid fa-times"></i>
                      </motion.button>
                  )}
                </AnimatePresence>
            </div>
        </div>

        {/* Global Mode Selector */}
        <div className="w-full mb-5">
            <ModeSelectorSection config={config} setConfig={setConfig} />
        </div>

        {/* Main Tabs (Hidden if searching) */}
        {!searchQuery && (
            <div className={`grid grid-cols-3 gap-1.5 p-1.5 w-full rounded-2xl mb-5 ${config.darkMode ? 'bg-black/40 border border-white/5' : 'bg-neutral-100'}`}>
                {(['SIM', 'AUDIO', 'DATA'] as const).map(tab => (
                  <button 
                    key={tab}
                    onClick={() => setActiveTab(tab)} 
                    className={`py-3 rounded-xl transition-all relative overflow-hidden text-[10px] font-bold uppercase tracking-wider ${activeTab === tab ? (config.darkMode ? 'text-white' : 'text-black') : 'text-neutral-500'}`}
                  >
                    {activeTab === tab && (
                      <motion.div 
                        layoutId="activeTab" 
                        className={`absolute inset-0 z-0 ${config.darkMode ? 'bg-white/10' : 'bg-white shadow-sm'}`} 
                      />
                    )}
                    <span className="relative z-10">{tab === 'SIM' ? 'Simulation' : tab === 'AUDIO' ? 'Audio Engine' : 'Saves & Cloud'}</span>
                  </button>
                ))}
            </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={searchQuery ? 'search' : activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            {/* --- SIMULATION TAB --- */}
            {(searchQuery || activeTab === 'SIM') && (
              <div className="w-full flex flex-col gap-5">
                
                {/* Quick Actions (Pause/Reset/Save) - Always visible in SIM */}
                <div className="flex items-center gap-3 w-full">
                    <button 
                      onClick={() => setIsPlaying(!isPlaying)} 
                      className={`h-14 flex-1 rounded-2xl flex items-center justify-center shadow-lg transition-all active:scale-95 group relative overflow-hidden ${isPlaying ? (config.darkMode ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-900 border border-zinc-200') : 'bg-indigo-600 text-white'}`}
                    >
                        <i className={`fa-solid ${isPlaying ? 'fa-pause' : 'fa-play'} text-xl relative z-10`}></i>
                        {!isPlaying && <div className="absolute inset-0 bg-white/10 animate-pulse" />}
                    </button>
                    <button onClick={onReset} className={`h-14 w-14 rounded-2xl shadow-sm border flex items-center justify-center hover:rotate-180 transition-all duration-700 active:scale-90 ${config.darkMode ? 'bg-zinc-800 border-white/5 text-white' : 'bg-white border-zinc-100 text-zinc-800'}`}>
                        <i className="fa-solid fa-rotate-right text-lg"></i>
                    </button>
                    <button onClick={onSave} className={`h-14 flex-1 rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-3 px-4 ${saveStatus === 'saved' ? 'bg-emerald-600/20 border border-emerald-500/50 text-emerald-400' : (config.darkMode ? 'bg-zinc-100 text-zinc-950 shadow-xl' : 'bg-zinc-900 text-white shadow-xl')}`}>
                        <i className={`fa-solid ${saveStatus === 'saved' ? 'fa-check' : 'fa-floppy-disk'} text-sm`}></i>
                        <span className="text-[10px] font-black uppercase tracking-wider">{saveStatus === 'saved' ? 'Config Saved' : 'Commit to Local'}</span>
                    </button>
                </div>

                {/* Sub-Tabs for SIM (Hidden if searching) */}
                {!searchQuery && (
                    <div className={`flex w-full p-1.5 rounded-2xl gap-1.5 ${config.darkMode ? 'bg-black/30 border border-white/5 shadow-inner' : 'bg-neutral-50 border border-neutral-200'}`}>
                        <SimSubTabButton id="GAMEPLAY" label="Play" icon="fa-gamepad" />
                        <SimSubTabButton id="PHYSICS" label="Physics" icon="fa-atom" />
                        <SimSubTabButton id="LOOKS" label="Looks" icon="fa-palette" />
                        <SimSubTabButton id="VIEW" label="View" icon="fa-eye" />
                        <SimSubTabButton id="OPTIMIZATION" label="Opti" icon="fa-bolt" />
                    </div>
                )}

                <div className="flex flex-col gap-4">
                  {/* 1. GAMEPLAY CONTENT */}
                  {(searchQuery || activeSimSubTab === 'GAMEPLAY') && (
                      <>
                          <CollapsibleSection title="Ball Placement" icon="fa-location-dot" darkMode={config.darkMode} defaultOpen>
                              <BallPlacementSection config={config} setConfig={setConfig} />
                          </CollapsibleSection>

                          {config.mode === 'RHYTHMIC_ESCAPE' && (
                              <CollapsibleSection title="Rhythmic Escape Hybrid" icon="fa-shield-halved" darkMode={config.darkMode} defaultOpen>
                                  <RhythmicEscapeSettingsSection config={config} setConfig={setConfig} />
                              </CollapsibleSection>
                          )}

                          {/* Specific Mode Settings - Context Aware */}
                          {(config.mode === 'RHYTHMIC' || config.mode === 'RHYTHMIC_ESCAPE') && (
                              <CollapsibleSection title="Rhythmic Engine" icon="fa-music" darkMode={config.darkMode} defaultOpen={config.mode === 'RHYTHMIC'}>
                                  <RhythmicModeSection config={config} setConfig={setConfig} onReset={onReset} />
                                  <RhythmicMidiFilterSection config={config} setConfig={setConfig} />
                              </CollapsibleSection>
                          )}

                          {(config.mode === 'SPAWNER' || searchQuery) && (
                              <CollapsibleSection title="Spawner Mechanics" icon="fa-plus-circle" darkMode={config.darkMode} defaultOpen={config.mode === 'SPAWNER'}>
                                  <SpawnerSettingsSection config={config} setConfig={setConfig} />
                              </CollapsibleSection>
                          )}

                          {(config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE' || searchQuery) && (
                              <CollapsibleSection title="Escape Mechanics" icon="fa-person-running" darkMode={config.darkMode} defaultOpen={config.mode === 'ESCAPE'}>
                                  <EscapeSettingsSection config={config} setConfig={setConfig} />
                                  <EscapeAdvancedSection config={config} setConfig={setConfig} />
                              </CollapsibleSection>
                          )}

                          {(config.mode === 'DRAWER' || searchQuery) && (
                              <CollapsibleSection title="Drawer Mechanics" icon="fa-pen-nib" darkMode={config.darkMode} defaultOpen={config.mode === 'DRAWER'}>
                                  <DrawerAdvancedSection config={config} setConfig={setConfig} />
                              </CollapsibleSection>
                          )}
                      </>
                  )}

                  {/* 2. PHYSICS CONTENT */}
                  {(searchQuery || activeSimSubTab === 'PHYSICS') && (
                      <PhysicsTab config={config} setConfig={setConfig} />
                  )}

                  {/* 3. LOOKS CONTENT */}
                  {(searchQuery || activeSimSubTab === 'LOOKS') && (
                      <VisualsTab config={config} setConfig={setConfig} />
                  )}

                  {/* 4. VIEW CONTENT */}
                  {(searchQuery || activeSimSubTab === 'VIEW') && (
                      <>
                          <CollapsibleSection title="HUD & Instrumentation" icon="fa-desktop" darkMode={config.darkMode} defaultOpen>
                              <HUDSettingsSection config={config} setConfig={setConfig} />
                          </CollapsibleSection>
                          
                          <CollapsibleSection title="Titles & Footers" icon="fa-heading" darkMode={config.darkMode}>
                              <TitleFooterSettingsSection config={config} setConfig={setConfig} />
                          </CollapsibleSection>

                          <CollapsibleSection title="Display & Camera" icon="fa-camera" darkMode={config.darkMode}>
                              <GeneralDisplaySection config={config} setConfig={setConfig} />
                              <CameraSettingsSection config={config} setConfig={setConfig} />
                          </CollapsibleSection>

                          <CollapsibleSection title="Base Background Layer" icon="fa-fill-drip" darkMode={config.darkMode}>
                              <BackgroundSettingsSection config={config} setConfig={setConfig} />
                          </CollapsibleSection>

                          <CollapsibleSection title="Background Parallax" icon="fa-mountain" darkMode={config.darkMode}>
                              <ParallaxSettingsSection config={config} setConfig={setConfig} />
                          </CollapsibleSection>
                      </>
                  )}

                  {/* 5. OPTIMIZATION CONTENT */}
                  {(searchQuery || activeSimSubTab === 'OPTIMIZATION') && (
                      <OptimizationSettingsSection config={config} setConfig={setConfig} />
                  )}
                </div>
              </div>
            )}
            
            {/* --- AUDIO TAB --- */}
            {(activeTab === 'AUDIO' && !searchQuery) && (
              <AudioStudioTab 
                config={config} 
                setConfig={setConfig} 
                onFileUpload={onFileUpload} 
                isAnalyzing={isAnalyzing} 
                analysisProgress={analysisProgress} 
                onBallSoundUpload={onBallSoundUpload} 
                isUploadingBallSound={isUploadingBallSound} 
                cloudSaves={cloudSaves}
              />
            )}
            
            {/* --- DATA TAB --- */}
            {(activeTab === 'DATA' && !searchQuery) && (
              <div className="flex flex-col gap-4 w-full">
                <StorageManager config={config} setConfig={setConfig} cloudSaves={cloudSaves} setCloudSaves={setCloudSaves} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
    </SettingsSearchContext.Provider>
  );
};
